import React, { useEffect, useState, useCallback, useRef } from 'react'
import { Link } from 'react-router-dom'
import { host } from './script/variables'
import './style/comments.css'

/* ─── Constants ────────────────────────────── */
const PAGE_SIZE = 3

/* ─── Menu action definitions ──────────────── */
// To add new actions in future: push a new object here + handle key in handleMenuAction
const COMMENT_MENU_ACTIONS = [
  {
    key:       'delete',
    label:     'Delete',
    className: 'cm-menu-item--danger',
    confirm:   'Delete this comment?',
    // icon: <TrashIcon /> — swap with your icon component
  },
  // { key: 'edit',   label: 'Edit',   className: '' },
  // { key: 'report', label: 'Report', className: 'cm-menu-item--danger' },
]

/* ─── Helpers ──────────────────────────────── */
function timeAgo(ts) {
  const diff = Date.now() - new Date(ts)
  const s  = Math.floor(diff / 1000)
  const m  = Math.floor(s / 60)
  const h  = Math.floor(m / 60)
  const d  = Math.floor(h / 24)
  const w  = Math.floor(d / 7)
  const mo = Math.floor(d / 30)
  if (mo > 0) return `${mo}mo ago`
  if (w  > 0) return `${w}w ago`
  if (d  > 0) return `${d}d ago`
  if (h  > 0) return `${h}h ago`
  if (m  > 0) return `${m}m ago`
  return s <= 5 ? 'just now' : `${s}s ago`
}

function getToken() {
  return localStorage.getItem('token') ?? ''
}

function getStoredUser() {
  try {
    const raw = localStorage.getItem('cafeuser')
    return raw ? JSON.parse(raw) : null
  } catch {
    return null
  }
}

function fullName(user) {
  if (!user) return ''
  
  const first = user.firstName  ?? ''
  const last  = user.lastName ?? ''
  if (!first && !last &&  user.username){return user.username}
  return [first, last].filter(Boolean).join(' ')
}

/* ─── Auto-grow textarea hook ──────────────── */
function useAutoGrow(ref) {
  return useCallback(() => {
    const el = ref.current
    if (!el) return
    el.style.height = 'auto'
    el.style.height = `${el.scrollHeight}px`
  }, [ref])
}

/* ─── Icons ────────────────────────────────── */
const HeartIcon = ({ filled }) =>
  filled ? (
    <svg viewBox="0 0 20 20" fill="#e53e3e" xmlns="http://www.w3.org/2000/svg" aria-hidden="true">
      <path d="M10 17.5s-7.5-4.7-7.5-10A5 5 0 0110 4.7 5 5 0 0117.5 7.5c0 5.3-7.5 10-7.5 10z" />
    </svg>
  ) : (
    <svg viewBox="0 0 20 20" fill="none" stroke="currentColor" strokeWidth="1.6" xmlns="http://www.w3.org/2000/svg" aria-hidden="true">
      <path d="M10 17.5s-7.5-4.7-7.5-10A5 5 0 0110 4.7 5 5 0 0117.5 7.5c0 5.3-7.5 10-7.5 10z" />
    </svg>
  )

const SendIcon = () => (
  <svg viewBox="0 0 20 20" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
    <path d="M18 2L9 11M18 2L12 18l-3-7-7-3 16-6z" />
  </svg>
)

const ChevronDownIcon = () => (
  <svg viewBox="0 0 14 14" fill="none" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" aria-hidden="true">
    <path d="M7 2v10M2 7l5 5 5-5" />
  </svg>
)

const DotsIcon = () => (
  <svg viewBox="0 0 20 20" fill="currentColor" width="16" height="16" aria-hidden="true">
    <circle cx="4"  cy="10" r="1.5" />
    <circle cx="10" cy="10" r="1.5" />
    <circle cx="16" cy="10" r="1.5" />
  </svg>
)

/* ─── Avatar ───────────────────────────────── */
const Avatar = ({ src, alt = '', className }) =>
  src ? (
    <img className={className} src={src} alt={alt} referrerPolicy="no-referrer" />
  ) : (
    <div className={`${className} cm-avatar-fallback`} aria-hidden="true">🧑</div>
  )

/* ─── ComposeBox ───────────────────────────── */
const ComposeBox = ({ user, placeholder, value, onChange, onSend, compact = false, disabled = false }) => {
  const taRef = useRef(null)
  const grow  = useAutoGrow(taRef)

  const handleKeyDown = (e) => {
    if (e.key === 'Enter' && (e.ctrlKey || e.metaKey)) {
      e.preventDefault()
      onSend()
    }
  }

  const canSend = value.trim().length > 0 && !disabled

  if (compact) {
    return (
      <div className="cm-reply-compose">
        <textarea
          ref={taRef}
          className="cm-reply-textarea"
          value={value}
          placeholder={placeholder}
          rows={1}
          disabled={disabled}
          onChange={e => { onChange(e.target.value); grow() }}
          onKeyDown={handleKeyDown}
          aria-label={placeholder}
        />
        <button
          className="cm-reply-send"
          onClick={onSend}
          disabled={!canSend}
          aria-label="Send reply"
        >
          <SendIcon />
        </button>
      </div>
    )
  }

  return (
    <div className="cm-compose">
      <Avatar src={user?.image} className="cm-compose-avatar" />
      <div className="cm-compose-right">
        <textarea
          ref={taRef}
          className="cm-textarea"
          value={value}
          placeholder={placeholder}
          rows={1}
          disabled={disabled}
          onChange={e => { onChange(e.target.value); grow() }}
          onKeyDown={handleKeyDown}
          aria-label={placeholder}
        />
        <div className="cm-compose-actions">
          <button
            className="cm-send-btn"
            onClick={onSend}
            disabled={!canSend}
            aria-label="Post comment"
          >
            <SendIcon />
            Post
          </button>
        </div>
      </div>
    </div>
  )
}

/* ─── CommentMenu ──────────────────────────── */
const CommentMenu = ({ commentId, onDeleted }) => {
  const [open,    setOpen]    = useState(false)
  const [loading, setLoading] = useState(false)
  const menuRef               = useRef(null)

  // Close on outside click or Escape key
  useEffect(() => {
    if (!open) return
    const onMouse = (e) => {
      if (menuRef.current && !menuRef.current.contains(e.target)) setOpen(false)
    }
    const onKey = (e) => { if (e.key === 'Escape') setOpen(false) }
    document.addEventListener('mousedown', onMouse)
    document.addEventListener('keydown',   onKey)
    return () => {
      document.removeEventListener('mousedown', onMouse)
      document.removeEventListener('keydown',   onKey)
    }
  }, [open])

  const handleMenuAction = async (action) => {
    if (action.confirm && !window.confirm(action.confirm)) return
    setOpen(false)

    if (action.key === 'delete') {
      await handleDelete()
    }
    // Future: if (action.key === 'edit')   { ... }
    // Future: if (action.key === 'report') { ... }
  }

  const handleDelete = async () => {
    setLoading(true)
    try {
      const res = await fetch(`${host}/actions/comment/delete`, {
        method:  'DELETE',
        headers: {
          'Content-Type': 'application/json',
          Authorization: `${getToken()}`,
        },
        body: JSON.stringify({ commentId }),
      })
      if (!res.ok) throw new Error(`Server error ${res.status}`)
      onDeleted(commentId)
    } catch (err) {
      console.error('[CommentMenu] delete failed:', err)
      alert('Could not delete comment. Please try again.')
    } finally {
      setLoading(false)
    }
  }

  return (
    <div className="cm-menu-wrap" ref={menuRef}>
      <button
        className="cm-menu-trigger"
        onClick={() => setOpen(prev => !prev)}
        aria-label="Comment options"
        aria-haspopup="true"
        aria-expanded={open}
        disabled={loading}
      >
        {loading
          ? <span className="cm-menu-spinner" aria-label="Deleting…" />
          : <DotsIcon />
        }
      </button>

      {open && (
        <ul className="cm-menu-dropdown" role="menu">
          {COMMENT_MENU_ACTIONS.map(action => (
            <li key={action.key} role="none">
              <button
                role="menuitem"
                className={`cm-menu-item ${action.className ?? ''}`}
                onClick={() => handleMenuAction(action)}
              >
                {action.label}
              </button>
            </li>
          ))}
        </ul>
      )}
    </div>
  )
}

/* ─── ReplyItem ────────────────────────────── */
const ReplyItem = ({
  repl, comId,
  user, like, isLikedComment,
  onLike, onReply,
  activeInnerReply, setActiveInnerReply,
  innerReplyVal, setInnerReplyVal,
  submitting,onDeleted
}) => {
  const isActive = activeInnerReply === repl._id

  return (
    <div className="cm-reply-item">
      <Avatar src={repl.authorImage} className="cm-reply-avatar" alt={repl.authorName} />
      <div className="cm-reply-body">
        <div className="cm-meta">
          <Link className="cm-author" to={`/user?id=${repl.authorEmail}`}>
            @{repl.authorName}
          </Link>
          <span className="cm-time">{timeAgo(repl.createdAt)}</span>
          {repl.isAuthor && (
            <CommentMenu
              commentId={repl._id}
              onDeleted={(id) => onDeleted(comId, id)}  // pass parent comId tooc
            />
          )}
        </div>

        <p className="cm-text">
          {repl.repliedToName && (
            <span className="cm-mention">@{repl.repliedToName} </span>
          )}
          {repl.commentText}
        </p>

        <div className="cm-footer">
          <button
            className={`cm-like-btn${isLikedComment.get(repl._id) ? ' liked' : ''}`}
            onClick={() => onLike(repl._id, repl.authorEmail, repl.commentText)}
            aria-label={`Like this reply. ${like.get(repl._id)?.like ?? 0} likes`}
            aria-pressed={!!isLikedComment.get(repl._id)}
          >
            <HeartIcon filled={!!isLikedComment.get(repl._id)} />
            <span>{like.get(repl._id)?.like ?? 0}</span>
          </button>

          <button
            className="cm-reply-btn"
            onClick={() => setActiveInnerReply(isActive ? null : repl._id)}
            aria-expanded={isActive}
          >
            {isActive ? 'Cancel' : 'Reply'}
          </button>
        </div>

        {isActive && (
          <ComposeBox
            user={user}
            placeholder={`@${repl.authorName}…`}
            value={innerReplyVal}
            onChange={setInnerReplyVal}
            onSend={() => onReply(comId, repl.authorEmail, repl.authorName)}
            disabled={submitting}
            compact
          />
        )}
      </div>
    </div>
  )
}

/* ═══════════════════════════════════════════════
   Main component
═══════════════════════════════════════════════ */
const Comments = ({ postid, commentCount, setCommentCount, isLogin, isBlog,isMoment }) => {
  /* ── State ── */
  const [stamp, setStamp]                   = useState(() => new Date().toISOString())
  const [isLoading, setIsLoading]           = useState(true)
  const [isLoadingMore, setIsLoadingMore]   = useState(false)
  const [hasMore, setHasMore]               = useState(true)
  const [comments, setComments]             = useState([])
  const [user, setUser]                     = useState(null)
  const [loadedCount, setLoadedCount]       = useState(0)

  const [replies, setReplies]               = useState(new Map())
  const [like, setLike]                     = useState(new Map())
  const [isLikedComment, setIsLiked]        = useState(new Map())
  const fetchingReplies                     = useRef(new Set())

  const [commentInput, setCommentInput]             = useState('')
  const [activeReply, setActiveReply]               = useState(null)
  const [replyInput, setReplyInput]                 = useState('')
  const [activeInnerReply, setActiveInnerReply]     = useState(null)
  const [innerReplyVal, setInnerReplyVal]           = useState('')

  const [submittingComment, setSubmittingComment]   = useState(false)
  const [submittingReply, setSubmittingReply]       = useState(false)

  const [error, setError] = useState(null)

  /* ── Merge helpers ── */
  const mergeLike = useCallback((existing, items) => {
    const m = new Map(existing)
    items.forEach(item => m.set(item._id, { like: item.likes ?? 0, dislikes: item.dislikes ?? 0 }))
    return m
  }, [])

  const mergeIsLiked = useCallback((existing, items) => {
    const m = new Map(existing)
    items.forEach(item => m.set(item._id, !!item.isLike))
    return m
  }, [])

  /* ── Remove comment from state (used by CommentMenu after delete) ── */
  const removeComment = useCallback((id) => {
    setComments(prev => prev.filter(c => c._id !== id))
    setReplies(prev  => { const m = new Map(prev); m.delete(id); return m })
    setLike(prev     => { const m = new Map(prev); m.delete(id); return m })
    setIsLiked(prev  => { const m = new Map(prev); m.delete(id); return m })
    setLoadedCount(c => Math.max(0, c - 1))
    setCommentCount(c => (typeof c === 'number' ? Math.max(0, c - 1) : c))
  }, [setCommentCount])

  const removeReply = useCallback((parentId, replyId) => {
  setReplies(prev => {
    const m = new Map(prev)
    const arr = m.get(parentId) ?? []
    m.set(parentId, arr.filter(r => r._id !== replyId))
    return m
  })
  setLike(prev    => { const m = new Map(prev); m.delete(replyId); return m })
  setIsLiked(prev => { const m = new Map(prev); m.delete(replyId); return m })
  // Decrement replyCount on parent comment
  setComments(prev =>
    prev.map(c => c._id === parentId
      ? { ...c, replyCount: Math.max(0, (c.replyCount ?? 0) - 1) }
      : c
    )
  )
}, [])

  /* ── Fetch top-level comments ── */
  const fetchComments = useCallback(async (currentStamp, isFirstLoad = false) => {
    if (isFirstLoad) setIsLoading(true)
    else setIsLoadingMore(true)
    setError(null)

    try {
      const res = await fetch(
        `${host}/actions/comment?stamp=${encodeURIComponent(currentStamp)}&cafeid=${encodeURIComponent(postid)}&il=${isLogin}`,
        { headers: { 'Content-Type': 'application/json', Authorization: getToken() } }
      )
      if (!res.ok) throw new Error(`Server error ${res.status}`)
      const data  = await res.json()
      const items = data.data ?? []

      if (items.length === 0) { setHasMore(false); return }

      const newItems = items.map(el => ({ ...el, level: 0 }))

      setComments(prev => {
        const existingIds = new Set(prev.map(c => c._id))
        return [...prev, ...newItems.filter(c => !existingIds.has(c._id))]
      })
      setReplies(prev => {
        const m = new Map(prev)
        newItems.forEach(el => { if (!m.has(el._id)) m.set(el._id, []) })
        return m
      })
      setLike(prev    => mergeLike(prev, items))
      setIsLiked(prev => mergeIsLiked(prev, items))
      setLoadedCount(prev => prev + items.length)
      setStamp(items[items.length - 1].createdAt)
      if (items.length < PAGE_SIZE) setHasMore(false)
    } catch (err) {
      setError('Failed to load comments. Please try again.')
      console.error('[Comments] fetchComments:', err)
    } finally {
      setIsLoading(false)
      setIsLoadingMore(false)
    }
  }, [postid, isLogin, mergeLike, mergeIsLiked])

  /* ── Fetch replies ── */
  const fetchReplies = useCallback(async (parentID, level) => {
    if (fetchingReplies.current.has(parentID)) return
    fetchingReplies.current.add(parentID)

    try {
      const res = await fetch(
        `${host}/actions/rply?pid=${encodeURIComponent(parentID)}&l=${level}&il=${isLogin}`,
        { headers: { 'Content-Type': 'application/json', Authorization: getToken() } }
      )
      if (!res.ok) throw new Error(`Server error ${res.status}`)
      const data  = await res.json()
      const items = data.data ?? []

      setReplies(prev => {
        const m   = new Map(prev)
        const arr = m.get(parentID) ?? []
        const existingIds = new Set(arr.map(r => r._id))
        m.set(parentID, [...arr, ...items.filter(r => !existingIds.has(r._id))])
        return m
      })
      setLike(prev    => mergeLike(prev, items))
      setIsLiked(prev => mergeIsLiked(prev, items))
      setComments(prev =>
        prev.map(c => c._id === parentID ? { ...c, level: c.level + 1 } : c)
      )
    } catch (err) {
      console.error('[Comments] fetchReplies:', err)
    } finally {
      fetchingReplies.current.delete(parentID)
    }
  }, [isLogin, mergeLike, mergeIsLiked])

  /* ── Add top-level comment ── */
  const addComment = async () => {
    if (!isLogin) { alert('Please log in to comment'); return }
    if (!commentInput.trim() || submittingComment) return

    setSubmittingComment(true)
    try {
      const res = await fetch(`${host}/actions/comment`, {
        method:  'POST',
        headers: { 'Content-Type': 'application/json', Authorization: getToken() },
        body: JSON.stringify({
          postID:      postid,
          depth:       0,
          parentID:    null,
          commentText: commentInput.trim(),
          repliedTo:   null,
          isBlog,
          isMoment,
          isRply:      false,
        }),
      })
      if (!res.ok) throw new Error(`Server error ${res.status}`)
      const data = await res.json()
      const id   = data.resp._id

      const newC = {
        _id:         id,
        postID:      postid,
        depth:       0,
        parentID:    null,
        authorName:  fullName(user),
        authorEmail: user.email,
        authorImage: user.image,
        commentText: commentInput.trim(),
        likes:       0,
        dislikes:    0,
        replyCount:  0,
        repliedTo:   null,
        createdAt:   new Date().toISOString(),
        level:       0,
        isAuthor:    true,   // current user just wrote this
      }

      setComments(prev  => [newC, ...prev])
      setReplies(prev   => { const m = new Map(prev); m.set(id, []); return m })
      setLike(prev      => { const m = new Map(prev); m.set(id, { like: 0, dislikes: 0 }); return m })
      setIsLiked(prev   => { const m = new Map(prev); m.set(id, false); return m })
      setCommentCount(c => (typeof c === 'number' ? c + 1 : c))
      setLoadedCount(c  => c + 1)
      setCommentInput('')
    } catch (err) {
      console.error('[Comments] addComment:', err)
      alert('Failed to post comment. Please try again.')
    } finally {
      setSubmittingComment(false)
    }
  }

  /* ── Add reply ── */
  const addReply = useCallback(async (comId, repliedTo, repliedToName, inputVal, clearFn) => {
    if (!isLogin) { alert('Please log in to reply'); return }
    if (!inputVal.trim() || submittingReply) return

    setSubmittingReply(true)
    try {
      const res = await fetch(`${host}/actions/comment`, {
        method:  'POST',
        headers: { 'Content-Type': 'application/json', Authorization: getToken() },
        body: JSON.stringify({
          postID:      postid,
          depth:       1,
          parentID:    comId,
          commentText: inputVal.trim(),
          repliedTo,
          repliedToName,
          isRply:      true,
          isBlog,
          isMoment,
        }),
      })
      if (!res.ok) throw new Error(`Server error ${res.status}`)
      const data = await res.json()
      const id   = data.resp._id

      const newR = {
        _id:          id,
        postID:       postid,
        depth:        1,
        parentID:     comId,
        isAuthor:     true,
        authorName:   fullName(user),
        authorEmail:  user.email,
        authorImage:  user.image,
        commentText:  inputVal.trim(),
        likes:        0,
        dislikes:     0,
        repliedTo,
        repliedToName,
        createdAt:    new Date().toISOString(),
        level:        0,
      }

      setReplies(prev => {
        const m   = new Map(prev)
        const arr = m.get(comId) ?? []
        m.set(comId, [newR, ...arr])
        return m
      })
      setComments(prev =>
        prev.map(c => c._id === comId ? { ...c, replyCount: (c.replyCount ?? 0) + 1 } : c)
      )
      setLike(prev    => { const m = new Map(prev); m.set(id, { like: 0, dislikes: 0 }); return m })
      setIsLiked(prev => { const m = new Map(prev); m.set(id, false); return m })
      clearFn()
    } catch (err) {
      console.error('[Comments] addReply:', err)
      alert('Failed to post reply. Please try again.')
    } finally {
      setSubmittingReply(false)
    }
  }, [isLogin, postid, isBlog, isMoment, user, submittingReply])

  /* ── Like / unlike ── */
  const likeComment = useCallback(async (comId, authorEmail, commentText) => {
    if (!isLogin) { alert('Please log in to like'); return }

    const wasLiked = isLikedComment.get(comId) ?? false

    setIsLiked(prev => { const m = new Map(prev); m.set(comId, !wasLiked); return m })
    setLike(prev => {
      const m   = new Map(prev)
      const cur = m.get(comId) ?? { like: 0, dislikes: 0 }
      m.set(comId, { ...cur, like: cur.like + (wasLiked ? -1 : 1) })
      return m
    })

    try {
      const res = await fetch(`${host}/actions/comment/like`, {
        method:  'POST',
        headers: { 'Content-Type': 'application/json', Authorization: getToken() },
        body: JSON.stringify({
          comId, isLike: wasLiked, authorEmail,
          cafeId: postid, comment: commentText, isBlog, isMoment 
        }),
      })
      if (!res.ok) throw new Error(`Server error ${res.status}`)
    } catch (err) {
      console.error('[Comments] likeComment:', err)
      setIsLiked(prev => { const m = new Map(prev); m.set(comId, wasLiked); return m })
      setLike(prev => {
        const m   = new Map(prev)
        const cur = m.get(comId) ?? { like: 0, dislikes: 0 }
        m.set(comId, { ...cur, like: cur.like + (wasLiked ? 1 : -1) })
        return m
      })
    }
  }, [isLogin, isLikedComment, postid, isBlog, isMoment])

  /* ── Init ── */
  useEffect(() => {
    setUser(getStoredUser())
    const initialStamp = new Date().toISOString()
    setStamp(initialStamp)
    fetchComments(initialStamp, true)
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [postid])

  /* ── Derived ── */
  const remaining = Math.max(0, (commentCount ?? 0) - loadedCount)

  /* ── Render ── */
  return (
    <div className="cm-root">

      {/* Compose */}
      <ComposeBox
        user={user}
        placeholder="Share your thoughts…"
        value={commentInput}
        onChange={setCommentInput}
        onSend={addComment}
        disabled={submittingComment}
      />

      {/* Error */}
      {error && (
        <div className="cm-error" role="alert">
          {error}
          <button onClick={() => fetchComments(stamp, comments.length === 0)}>Retry</button>
        </div>
      )}

      {/* Thread list */}
      {isLoading ? (
        <div className="cm-loading" aria-live="polite" aria-label="Loading comments">
          <div className="cm-spinner" />
          Loading comments…
        </div>
      ) : comments.length === 0 ? (
        <div className="cm-empty" aria-live="polite">
          <div className="cm-empty-icon" aria-hidden="true">💬</div>
          <p>No comments yet. Be the first!</p>
        </div>
      ) : (
        <div className="cm-thread-list" role="list">
          {comments.map((com, idx) => {
            const rplyList       = replies.get(com._id) ?? []
            const isReplying     = activeReply === com._id
            const fetchedReplies = rplyList.length
            const totalReplies   = com.replyCount ?? 0
            const moreReplies    = com.level > 0
              ? Math.max(0, totalReplies - fetchedReplies)
              : 0

            return (
              <div
                className="cm-comment"
                key={com._id}
                role="listitem"
                style={{ animationDelay: `${Math.min(idx, 10) * 40}ms` }}
              >
                {/* Avatar col + thread line */}
                <div className="cm-avatar-col">
                  <Avatar src={com.authorImage} className="cm-avatar" alt={com.authorName} />
                  {(rplyList.length > 0 || isReplying) && (
                    <div className="cm-thread-line" aria-hidden="true" />
                  )}
                </div>

                <div className="cm-body">
                  {/* Meta */}
                  <div className="cm-meta">
                    <Link className="cm-author" to={`/user?id=${com.authorEmail}`}>
                      @{com.authorName}
                    </Link>
                    <span className="cm-time">{timeAgo(com.createdAt)}</span>

                    {/* 3-dot menu — only visible to the comment author */}
                    {com.isAuthor && (
                      <CommentMenu
                        commentId={com._id}
                        onDeleted={removeComment}
                      />
                    )}
                  </div>

                  {/* Text */}
                  <p className="cm-text">{com.commentText}</p>

                  {/* Footer */}
                  <div className="cm-footer">
                    <button
                      className={`cm-like-btn${isLikedComment.get(com._id) ? ' liked' : ''}`}
                      onClick={() => likeComment(com._id, com.authorEmail, com.commentText)}
                      aria-label={`Like. ${like.get(com._id)?.like ?? 0} likes`}
                      aria-pressed={!!isLikedComment.get(com._id)}
                    >
                      <HeartIcon filled={!!isLikedComment.get(com._id)} />
                      <span>{like.get(com._id)?.like ?? 0}</span>
                    </button>

                    <button
                      className="cm-reply-btn"
                      aria-expanded={isReplying}
                      onClick={() => {
                        const willOpen = !isReplying
                        setActiveReply(willOpen ? com._id : null)
                        setReplyInput('')
                        setActiveInnerReply(null)
                        if (willOpen && rplyList.length === 0 && totalReplies > 0) {
                          fetchReplies(com._id, com.level)
                        }
                      }}
                    >
                      {isReplying ? 'Cancel' : 'Reply'}
                    </button>
                  </div>

                  {/* Inline reply compose */}
                  {isReplying && (
                    <ComposeBox
                      user={user}
                      placeholder={`Replying to @${com.authorName}…`}
                      value={replyInput}
                      onChange={setReplyInput}
                      onSend={() =>
                        addReply(
                          com._id, com.authorEmail, com.authorName,
                          replyInput,
                          () => { setReplyInput(''); setActiveReply(null) }
                        )
                      }
                      disabled={submittingReply}
                      compact
                    />
                  )}

                  {/* Replies list */}
                  {rplyList.length > 0 && (
                    <div className="cm-replies" role="list" aria-label={`Replies to ${com.authorName}`}>
                      {rplyList.map(repl => (
                        <ReplyItem
                          key={repl._id}
                          repl={repl}
                          comId={com._id}
                          user={user}
                          like={like}
                          isLikedComment={isLikedComment}
                          onLike={likeComment}
                          onReply={(comId, rEmail, rName) =>
                            addReply(
                              comId, rEmail, rName,
                              innerReplyVal,
                              () => { setInnerReplyVal(''); setActiveInnerReply(null) }
                            )
                          }
                          activeInnerReply={activeInnerReply}
                          setActiveInnerReply={setActiveInnerReply}
                          innerReplyVal={innerReplyVal}
                          setInnerReplyVal={setInnerReplyVal}
                          submitting={submittingReply}
                          onDeleted={removeReply}
                        />
                      ))}

                      {moreReplies > 0 && (
                        <button
                          className="cm-load-more"
                          onClick={() => fetchReplies(com._id, com.level)}
                          aria-label={`Load ${moreReplies} more ${moreReplies === 1 ? 'reply' : 'replies'}`}
                        >
                          <ChevronDownIcon />
                          {moreReplies} more {moreReplies === 1 ? 'reply' : 'replies'}
                        </button>
                      )}
                    </div>
                  )}

                  {rplyList.length === 0 && totalReplies > 0 && !isReplying && (
                    <button
                      className="cm-load-more"
                      style={{ marginTop: 8 }}
                      onClick={() => fetchReplies(com._id, com.level)}
                      aria-label={`Show ${totalReplies} ${totalReplies === 1 ? 'reply' : 'replies'}`}
                    >
                      <ChevronDownIcon />
                      {totalReplies} {totalReplies === 1 ? 'reply' : 'replies'}
                    </button>
                  )}
                </div>
              </div>
            )
          })}
        </div>
      )}

      {/* Load more comments */}
      {!isLoading && hasMore && remaining > 0 && (
        <div className="cm-more-comments">
          <button
            className="cm-more-btn"
            onClick={() => fetchComments(stamp)}
            disabled={isLoadingMore}
            aria-label={`Load ${remaining} more comment${remaining !== 1 ? 's' : ''}`}
          >
            {isLoadingMore ? (
              <div className="cm-spinner cm-spinner-sm" aria-hidden="true" />
            ) : (
              <svg viewBox="0 0 16 16" fill="none" stroke="currentColor" strokeWidth="1.6" strokeLinecap="round" width="14" height="14" aria-hidden="true">
                <path d="M8 2v12M3 8l5 6 5-6" />
              </svg>
            )}
            {isLoadingMore ? 'Loading…' : `Load ${remaining} more comment${remaining !== 1 ? 's' : ''}`}
          </button>
        </div>
      )}
    </div>
  )
}

export default Comments